import { FC, useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import MainLayout from 'src/pages/private';
import { useFormik } from 'formik';
import { grnPaymentInitialValues } from 'src/utils/initialValues.utils';
import { grnPaymentDynamicValidation } from 'src/utils/validationScheme.utils';
import { calculateRemainingAmount } from 'src/utils/grnPayment.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  apiUrls,
  routeURL,
  successMessages,
  pageTitles,
  grnPaymentDialogTitles,
  grnPaymentDialogSubtitles,
  pagination,
  DATE_FORMATS,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import GRNPaymentGrid from 'src/components/Grids/GRNPaymentGrid';
import AddGRNPaymentDialog from 'src/components/DialogBox/GRNPaymentDialog/AddGRNPaymentDialog';
import TextField from 'src/components/InputFields/TextField';
import PaymentModePicker from 'src/components/InputFields/PaymentModePicker';
import {
  formatNumberWithCommas,
  handleNumberFieldChange,
} from 'src/utils/format.utils';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import {
  PageHeaderBox,
  TitleSection,
  BackButton,
  BackIcon,
  PageTitle,
  ButtonIcon,
  GRNPageTitle,
} from 'src/components/StyledComponents/GRNPayments.styles';
import FormikSelectField from 'src/components/InputFields/FormikSelectField';
import { useGetBank } from 'src/services/bank.service';
import {
  FlexColumnBox,
  FlexColumnBoxGrn,
} from 'src/components/DialogBox/POBox/POBox.styles';
import { PrimaryActionButton } from 'src/components/StyledComponents/StyledPageComponents';
import { DateLabel } from 'src/components/DialogBox/GRNPaymentDialog/GRNPaymentDialog.styles';
import { StyledDatePickerErrorText } from 'src/components/StyledComponents/StyledUnitPicker';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import {
  GRNPaymentPayload,
  LocationState,
} from 'src/models/Management.interface';

const GRNPayments: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { grnId } = useParams<{ grnId: string }>();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingGRNDetails, setFetchingGRNDetails] = useState(false);
  const [paymentAdded, setPaymentAdded] = useState(false);
  const [resetPageSignal, setResetPageSignal] = useState(0);
  const locationState = location.state as LocationState | null;
  const [invoiceNumber, setInvoiceNumber] = useState<string>(
    locationState?.invoiceNumber || ''
  );
  const [invoiceAmount, setInvoiceAmount] = useState<number>(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState<number>(0);
  const { showSnackbar } = StyledSnackbar();
  const [filterValues, setFilterValues] = useState({
    status: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });

  const { bankBasics, fetchBasicDetails } = useGetBank();

  const accountOptions = bankBasics.map((bank) => ({
    label: `${bank.bankName} (${bank.accountNumber})`,
    value: bank.id,
  }));

  // Fetch GRN details to get invoice number and amounts
  const fetchGRNDetails = async () => {
    if (grnId) {
      setFetchingGRNDetails(true);
      try {
        const response = await axiosTokenInstance.get(apiUrls.GET_GRN_BY_ID, {
          params: { id: grnId },
        });
        if (!locationState?.invoiceNumber) {
          setInvoiceNumber(response.data?.data?.invoiceNumber || '');
        }
        setInvoiceAmount(Number(response.data?.data?.invoiceAmount) || 0);
        setTotalPaidAmount(Number(response.data?.data?.totalPaidAmount) || 0);
      } catch (error) {
        const errorMessage = await getApiErrorMessage(error);
        showSnackbar(`Failed to load GRN details. ${errorMessage}`, 'error');
      } finally {
        setFetchingGRNDetails(false);
      }
    }
  };

  // No currentPaymentAmount for new payments; only needed when editing existing ones
  const remainingAmount = calculateRemainingAmount(
    invoiceAmount,
    totalPaidAmount
  );

  const handleClickOpen = async () => {
    await fetchGRNDetails();
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const handleBack = () => {
    navigate(`${routeURL.USER + routeURL.GRN_MANAGEMENT}?view=${grnId}`);
  };

  const validationSchema = useMemo(
    () => grnPaymentDynamicValidation(remainingAmount),
    [remainingAmount]
  );

  const formik = useFormik({
    initialValues: grnPaymentInitialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Submission handled in handleSavePayment
    },
  });

  const handleSavePayment = async () => {
    const touchedFields: Record<string, boolean> = {
      receivedBy: true,
      paymentDate: true,
      amount: true,
      paymentMode: true,
    };

    if (formik.values.paymentMode === PaymentMode.CHEQUE) {
      touchedFields.chequeNumber = true;
      touchedFields.chequeDate = true;
      touchedFields.bankAccountId = true;
    } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
      touchedFields.bankAccountId = true;
      touchedFields.transactionReference = true;
    }

    // Validate using Formik's validateForm method
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(touchedFields, false);
      return;
    }

    setLoading(true);

    try {
      const payload: GRNPaymentPayload = {
        grnId: grnId!,
        receiverName: formik.values.receivedBy,
        paymentDate: dayjs(formik.values.paymentDate).format(DATE_FORMATS.API),
        amount: parseFloat(formik.values.amount),
        paymentMode: formik.values.paymentMode,
      };

      if (formik.values.paymentMode === PaymentMode.CHEQUE) {
        payload.chequeNumber = formik.values.chequeNumber;
        payload.chequeDate = dayjs(formik.values.chequeDate).format(
          DATE_FORMATS.API
        );
        payload.bankAccountId = formik.values.bankAccountId;
      } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
        payload.bankAccountId = formik.values.bankAccountId;
        payload.paymentRefNo = formik.values.transactionReference;
      }

      const response = await axiosTokenInstance.post(
        apiUrls.ADD_GRN_PAYMENT,
        payload
      );
      showSnackbar(response.data.message, 'success');
      setResetPageSignal((prev) => prev + 1);
      setPaymentAdded(!paymentAdded);
      handleClose();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const mode = formik.values.paymentMode;
    if (mode === PaymentMode.CHEQUE || mode === PaymentMode.ONLINE) {
      fetchBasicDetails();
    }
  }, [formik.values.paymentMode]);

  return (
    <MainLayout title="GRN Management">
      {!grnId ? (
        <Typography>Invalid GRN ID</Typography>
      ) : (
        <>
          <PageHeaderBox>
            <TitleSection>
              <BackButton onClick={handleBack}>
                <BackIcon component="i" className="bx bx-chevron-left" />
              </BackButton>
              <PageTitle>{pageTitles.GRN_PAYMENTS}</PageTitle>{' '}
              <GRNPageTitle>#{invoiceNumber}</GRNPageTitle>
            </TitleSection>
            <PrimaryActionButton
              className="btn-primary"
              onClick={handleClickOpen}
              disabled={fetchingGRNDetails}
            >
              <ButtonIcon component="i" className="bx bx-plus 1" />
              New GRN Payment
            </PrimaryActionButton>
          </PageHeaderBox>
          <GRNPaymentGrid
            grnId={grnId}
            state={paymentAdded}
            filters={filterValues}
            resetPageSignal={resetPageSignal}
          />
          <AddGRNPaymentDialog
            open={open}
            handleClose={handleClose}
            title={grnPaymentDialogTitles.ADD}
            subtitle={grnPaymentDialogSubtitles.ADD}
            onSubmit={formik.handleSubmit}
            buttonDisabled={loading || fetchingGRNDetails}
            buttonLabel="Save"
            onSaveClick={handleSavePayment}
            paymentModeSection={
              <>
                <PaymentModePicker
                  color="var(--subtext)"
                  width="251px"
                  id="paymentMode"
                  label="Payment Method"
                  formikProps={formik}
                  customMessage="Select payment method"
                  showLabel={false}
                />
                {formik.values.paymentMode === PaymentMode.CHEQUE && (
                  <>
                    <Box
                      display={'flex'}
                      gap={'16px'}
                      justifyContent={'space-between'}
                      mb={0}
                      mt={-3}
                    >
                      <FlexColumnBoxGrn>
                        <TextField
                          color="var(--subtext)"
                          width="251px"
                          id="chequeNumber"
                          label="Cheque Number"
                          formikProps={formik}
                        />
                      </FlexColumnBoxGrn>
                      <FlexColumnBoxGrn>
                        <DateLabel>Cheque Date</DateLabel>
                        <CreatedDatePicker
                          width="100%"
                          height="40px"
                          fontSize="16px"
                          color="var(--subtext)"
                          name="chequeDate"
                          placeholder="Cheque Date"
                          formikProps={formik}
                        />
                      </FlexColumnBoxGrn>
                    </Box>
                    <Box mb={0}>
                      <FormikSelectField
                        id="bankAccountId"
                        label="Bank Account"
                        formikProps={formik}
                        width="251px"
                        options={accountOptions}
                      />
                    </Box>
                  </>
                )}
                {formik.values.paymentMode === PaymentMode.ONLINE && (
                  <Box
                    display={'flex'}
                    gap={'16px'}
                    justifyContent={'space-between'}
                    mb={0}
                  >
                    <FlexColumnBoxGrn>
                      <FormikSelectField
                        id="bankAccountId"
                        label="Bank Account"
                        formikProps={formik}
                        width="251px"
                        options={accountOptions}
                      />
                    </FlexColumnBoxGrn>
                    <FlexColumnBoxGrn>
                      <TextField
                        color="var(--subtext)"
                        width="251px"
                        id="transactionReference"
                        label="Payment Reference"
                        formikProps={formik}
                      />
                    </FlexColumnBoxGrn>
                  </Box>
                )}
              </>
            }
          >
            <Box display={'flex'} gap={'16px'} mb={0}>
              <FlexColumnBox>
                <TextField
                  color="var(--subtext)"
                  width="270px"
                  id="receivedBy"
                  label="Received By"
                  formikProps={formik}
                  mb={2}
                />
              </FlexColumnBox>
              <FlexColumnBoxGrn>
                <CreatedDatePicker
                  label="Payment Date"
                  showLabel={true}
                  color="var(--subtext)"
                  width="100%"
                  height="40px"
                  name="paymentDate"
                  placeholder="Payment Date"
                  formikProps={formik}
                  mb={2}
                />
                {formik.touched.paymentDate && formik.errors.paymentDate && (
                  <StyledDatePickerErrorText>
                    <ErrorIcon />
                    {formik.errors.paymentDate}
                  </StyledDatePickerErrorText>
                )}
              </FlexColumnBoxGrn>
            </Box>
            <Box mb={0}>
              <TextField
                color="var(--subtext)"
                width="270px"
                id="amount"
                label="Amount"
                formikProps={formik}
                type="text"
                value={formatNumberWithCommas(formik.values.amount)}
                onChange={(e) => handleNumberFieldChange(e, formik, 'amount')}
                placeholder={
                  remainingAmount > 0
                    ? `Remaining: ${remainingAmount.toLocaleString()}`
                    : ''
                }
              />
            </Box>
          </AddGRNPaymentDialog>
        </>
      )}
    </MainLayout>
  );
};

export default GRNPayments;
